import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Mail, Phone, Leaf, ShieldCheck } from "lucide-react";
import { COOMFLONA_LOGO, INSTAGRAM, INSTAGRAM_URL, whatsappLink } from "../lib/constants";

export default function Footer() {
  return (
    <footer className="relative bg-forest-900 text-cream-100 mt-24 noise-overlay overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-copaiba-500/10 blur-3xl" aria-hidden />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-terracotta-500/10 blur-3xl" aria-hidden />
      <div className="container-page relative py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            <img src={COOMFLONA_LOGO} alt="COOMFLONA" className="h-16 w-16 object-contain" />
            <div>
              <div className="font-display text-2xl leading-tight">COOMFLONA</div>
              <div className="text-xs uppercase tracking-[0.22em] text-cream-100/70">
                Cooperativa Mista da Flona do Tapajós
              </div>
            </div>
          </div>
          <p className="text-cream-100/80 leading-relaxed max-w-lg">
            Há 21 anos unindo comunidades tradicionais da Floresta Nacional do Tapajós em torno do manejo florestal
            100% comunitário. Fundada em 2005, com apoio técnico do Projeto Promanejo, hoje somos 305 cooperados e
            beneficiamos mais de 1.000 famílias em Belterra (PA).
          </p>
          <div className="flex items-center gap-2 mt-6 text-copaiba-300 text-sm">
            <Leaf className="w-4 h-4" />
            <span>Manejo florestal comunitário &middot; Rastreabilidade total</span>
          </div>
        </div>

        <div>
          <h4 className="font-display text-xl mb-5">Navegação</h4>
          <ul className="space-y-3 text-sm text-cream-100/80">
            <li><Link to="/" className="hover:text-copaiba-300 transition-colors" data-testid="footer-home-link">Início</Link></li>
            <li><Link to="/sobre" className="hover:text-copaiba-300 transition-colors" data-testid="footer-sobre-link">Sobre nós</Link></li>
            <li><Link to="/noticias" className="hover:text-copaiba-300 transition-colors" data-testid="footer-noticias-link">Notícias</Link></li>
            <li><Link to="/eco-loja" className="hover:text-copaiba-300 transition-colors" data-testid="footer-eco-loja-link">Eco Loja Tapajós</Link></li>
            <li><Link to="/instagram" className="hover:text-copaiba-300 transition-colors" data-testid="footer-instagram-page-link">Instagram</Link></li>
            <li><Link to="/vagas" className="hover:text-copaiba-300 transition-colors" data-testid="footer-vagas-link">Vagas de emprego</Link></li>
            <li><Link to="/secretaria" className="hover:text-copaiba-300 transition-colors" data-testid="footer-secretaria-link">Secretaria Virtual</Link></li>
            <li><Link to="/contato" className="hover:text-copaiba-300 transition-colors" data-testid="footer-contato-link">Contato</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-xl mb-5">Contato</h4>
          <ul className="space-y-3 text-sm text-cream-100/80">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-copaiba-300" /> Floresta Nacional do Tapajós — Belterra (PA), Brasil</li>
            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-copaiba-300" />
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="hover:text-copaiba-300" data-testid="footer-whatsapp-link">WhatsApp de atendimento</a>
            </li>
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-copaiba-300" /> contato@coomflona.com.br</li>
            <li className="flex items-start gap-2"><Instagram className="w-4 h-4 mt-0.5 text-copaiba-300" />
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-copaiba-300" data-testid="footer-instagram-link">@{INSTAGRAM}</a>
            </li>
          </ul>

          {/* Certificação Florestal — FSC */}
          <div className="mt-8 pt-6 border-t border-cream-100/15" data-testid="footer-fsc">
            <h4 className="font-display text-xl mb-4">Certificação Florestal</h4>
            <div className="flex items-start gap-4">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_cooperative-web-1/artifacts/a72ff9676acb0f34_selo%20fsc.jpeg"
                alt="Selo FSC — A marca do manejo florestal responsável — FSC C116124"
                className="w-24 shrink-0 object-contain"
                data-testid="footer-fsc-logo"
              />
              <p className="text-xs text-cream-100/80 leading-relaxed">
                Nossos produtos e processos seguem os princípios do <b>FSC<sup>®</sup></b>, contribuindo
                para a conservação da floresta e para o desenvolvimento das comunidades.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Selo de Segurança HTTPS */}
      <div className="border-t border-cream-100/10 bg-forest-900/60">
        <div className="container-page py-5 flex flex-col md:flex-row items-center justify-between gap-5">
          <Link
            to="/seguranca"
            data-testid="security-badge"
            className="group flex items-center gap-3 hover:text-copaiba-300 transition-colors w-full md:w-auto"
          >
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-xl bg-copaiba-500/15 border border-copaiba-300/40 flex items-center justify-center group-hover:bg-copaiba-500/25">
                <ShieldCheck className="w-5 h-5 text-copaiba-300" strokeWidth={2.2} aria-hidden />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-copaiba-500 border border-forest-900 flex items-center justify-center">
                <svg viewBox="0 0 24 24" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" className="text-forest-900" aria-hidden>
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              </div>
            </div>
            <div className="leading-tight">
              <div className="font-display text-sm tracking-wide text-cream-100">SITE SEGURO</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-copaiba-300/85">Conexão protegida por HTTPS</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-cream-100/55">Seus dados mais protegidos</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-xs text-cream-100/60 text-center md:text-right">
            <div>© {new Date().getFullYear()} COOMFLONA — Cooperativa Mista da Flona do Tapajós. Todos os direitos reservados.<br className="hidden md:block" />
              <span data-testid="footer-dev-credit">Site desenvolvido por <b className="text-copaiba-300">Railan Farias</b></span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-cream-100/10">
        <div className="container-page py-3 flex items-center justify-end text-xs text-cream-100/60">
          <Link to="/admin/login" className="hover:text-copaiba-300" data-testid="footer-admin-link">Área restrita</Link>
        </div>
      </div>
    </footer>
  );
}
